import React, { useEffect, useState, useMemo } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { ResourceCard } from "@/components/ResourceCard";
import { HERO_POSTER } from "@/lib/assets";

const GEOS = ["all", "Houston, TX", "Texas", "National"];
const OWNERS = [["all", "All sources"], ["foundation", "Foundation Programs"], ["third-party", "Verified Third-Party"]];
const DEADLINES = [["all", "Any deadline"], ["rolling", "Rolling / Open"], ["closing-soon", "Closing Soon"], ["varies", "Varies"], ["none", "No deadline"]];

export default function Marketplace() {
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [geography, setGeography] = useState("all");
  const [owner, setOwner] = useState("all");
  const [deadlineType, setDeadlineType] = useState("all");

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category !== "all") params.category = category;
    if (geography !== "all") params.geography = geography;
    if (owner !== "all") params.owner = owner;
    if (deadlineType !== "all") params.deadline_type = deadlineType;
    const t = setTimeout(() => {
      api.get("/resources", { params }).then((r) => { setResources(r.data); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, category, geography, owner, deadlineType]);

  const catName = useMemo(() => Object.fromEntries(categories.map((c) => [c.slug, c.name])), [categories]);
  const activeFilters = [category, geography, owner, deadlineType].filter((v) => v !== "all").length + (q ? 1 : 0);

  const reset = () => { setQ(""); setCategory("all"); setGeography("all"); setOwner("all"); setDeadlineType("all"); };

  return (
    <main>
      <PageHero overline="Community Opportunity Marketplace" image={HERO_POSTER}
        title="Find verified opportunities, all in one place"
        subtitle="Search grants, housing help, scholarships, mentorship, and more — every listing verified and clearly attributed to its provider.">
        <div className="mt-8 bg-white rounded-2xl p-2 flex items-center gap-2 max-w-2xl shadow-2xl">
          <Search className="h-5 w-5 text-slate-400 ml-3" />
          <Input data-testid="opportunity-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search programs, organizations, keywords..."
            className="border-0 focus-visible:ring-0 text-navy-900 text-base" />
        </div>
      </PageHero>

      {/* Category pills */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-10">
        <div className="flex flex-wrap gap-2.5" data-testid="opportunity-category-filter">
          <button onClick={() => setCategory("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${category === "all" ? "bg-navy-900 text-white border-navy-900" : "bg-white text-navy-800 border-slate-200 hover:border-navy-700"}`}>
            All Categories
          </button>
          {categories.map((c) => (
            <button key={c.slug} data-testid={`category-pill-${c.slug}`} onClick={() => setCategory(c.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${category === c.slug ? "bg-navy-900 text-white border-navy-900" : "bg-white text-navy-800 border-slate-200 hover:border-navy-700"}`}>
              {c.name} <span className="opacity-60">({c.count})</span>
            </button>
          ))}
        </div>

        {/* Filter dropdowns */}
        <div className="flex flex-wrap items-center gap-3 mt-6 pb-2">
          <span className="flex items-center gap-1.5 text-sm text-slate-500 font-medium"><SlidersHorizontal className="h-4 w-4" /> Filters:</span>
          <Select value={geography} onValueChange={setGeography}>
            <SelectTrigger data-testid="filter-geography" className="w-[160px] rounded-full h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{GEOS.map((g) => <SelectItem key={g} value={g}>{g === "all" ? "All locations" : g}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger data-testid="filter-owner" className="w-[190px] rounded-full h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{OWNERS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={deadlineType} onValueChange={setDeadlineType}>
            <SelectTrigger data-testid="filter-deadline" className="w-[160px] rounded-full h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{DEADLINES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          {activeFilters > 0 && (
            <Button variant="ghost" size="sm" onClick={reset} className="rounded-full text-slate-500" data-testid="filter-reset">
              <X className="h-4 w-4 mr-1" /> Clear ({activeFilters})
            </Button>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 pb-24">
        <p className="text-sm text-slate-500 mb-6" data-testid="results-count">
          {loading ? "Loading..." : `${resources.length} opportunit${resources.length === 1 ? "y" : "ies"} found`}
        </p>
        {!loading && resources.length === 0 ? (
          <div className="text-center py-24 bg-slate-50 rounded-3xl">
            <p className="font-display text-2xl text-navy-900 font-bold">No opportunities match your filters</p>
            <p className="text-slate-500 mt-2">Try adjusting your search or clearing filters.</p>
            <Button onClick={reset} className="mt-6 rounded-full bg-navy-900 text-white">Clear filters</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {resources.map((r) => <ResourceCard key={r.id} r={r} categoryName={catName[r.category]} />)}
          </div>
        )}
      </section>
    </main>
  );
}
